// Stores FileSystemFileHandle objects and published schemas in IndexedDB.
// Using IndexedDB instead of localStorage for schemas means they survive
// localStorage.clear() (called by "Update Build Version").
const sessionHandles = new Map<string, FileSystemFileHandle>()

const DB_NAME = 'formEngineDB'
const STORE_NAME = 'fileHandles'
const SCHEMA_STORE = 'publishedSchemas'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // Version 2 adds the publishedSchemas store alongside fileHandles
    const req = indexedDB.open(DB_NAME, 2)
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME)
      if (!db.objectStoreNames.contains(SCHEMA_STORE)) db.createObjectStore(SCHEMA_STORE)
    }
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result)
    req.onerror = () => reject(req.error)
  })
}

async function savePublishedSchemaToIDB(formType: string, schema: object): Promise<void> {
  try {
    const db = await openDB()
    await new Promise<void>((resolve) => {
      const tx = db.transaction(SCHEMA_STORE, 'readwrite')
      tx.objectStore(SCHEMA_STORE).put(JSON.stringify(schema), formType)
      tx.oncomplete = () => resolve()
    })
  } catch { /* ignore */ }
}

// Call on page mount: if localStorage lost the schema, restore it from IDB
export async function syncPublishedSchemaFromIDB(formType: string): Promise<boolean> {
  try {
    const lsKey = `formEngine_published_${formType}`
    if (localStorage.getItem(lsKey)) return false   // already present, nothing to do
    const db = await openDB()
    const raw = await new Promise<string | null>((resolve) => {
      const tx = db.transaction(SCHEMA_STORE, 'readonly')
      const req = tx.objectStore(SCHEMA_STORE).get(formType)
      req.onsuccess = () => resolve((req.result as string) ?? null)
      req.onerror = () => resolve(null)
    })
    if (raw) {
      localStorage.setItem(lsKey, raw)
      return true    // restored
    }
    return false
  } catch {
    return false
  }
}

export async function getStoredHandle(formType: string): Promise<FileSystemFileHandle | null> {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(formType)
      req.onsuccess = () => resolve((req.result as FileSystemFileHandle) ?? null)
      req.onerror = () => resolve(null)
    })
  } catch {
    return null
  }
}

async function storeHandle(formType: string, handle: FileSystemFileHandle): Promise<void> {
  try {
    const db = await openDB()
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).put(handle, formType)
      tx.oncomplete = () => resolve()
    })
  } catch { /* ignore */ }
}

export async function clearHandle(formType: string): Promise<void> {
  sessionHandles.delete(formType)
  try {
    const db = await openDB()
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).delete(formType)
      tx.oncomplete = () => resolve()
    })
  } catch { /* ignore */ }
}

// Maps formType → suggested filename shown in the save dialog (first run only)
export const FORM_TYPE_FILE_NAMES: Record<string, string> = {
  grant:    'grant-form.tsx',
  case:     'create-case-form.tsx',
  training: 'create-training-form.tsx',
}

export type PublishResult = { success: true } | { success: false; error: string }

export async function publishToFile(formType: string, content: string): Promise<PublishResult> {
  try {
    // Check in-memory session cache first (avoids permission prompt within same tab)
    let handle: FileSystemFileHandle | null = sessionHandles.get(formType) ?? null

    if (!handle) {
      handle = await getStoredHandle(formType)
    }

    if (handle) {
      let perm: PermissionState = 'denied'
      try {
        perm = await (handle as any).queryPermission({ mode: 'readwrite' })
        if (perm !== 'granted') {
          perm = await (handle as any).requestPermission({ mode: 'readwrite' })
        }
      } catch { /* handle may be stale */ }

      if (perm !== 'granted') {
        await clearHandle(formType)
        sessionHandles.delete(formType)
        handle = null
      }
    }

    if (!handle) {
      handle = await (window as any).showSaveFilePicker({
        suggestedName: FORM_TYPE_FILE_NAMES[formType] ?? `${formType}-form.tsx`,
        types: [{ description: 'TypeScript React', accept: { 'text/plain': ['.tsx', '.ts'] } }],
      }) as FileSystemFileHandle
      await storeHandle(formType, handle)
    }

    // Cache in session so next publish skips IndexedDB + permission prompt
    sessionHandles.set(formType, handle)

    const writable = await (handle as any).createWritable()
    await writable.write(content)
    await writable.close()

    return { success: true }
  } catch (err: any) {
    if (err?.name === 'AbortError') return { success: false, error: 'cancelled' }
    return { success: false, error: String(err) }
  }
}

// Markers used to do surgical in-place updates instead of replacing the whole file
export const FORM_ENGINE_START = '{/* FORM_ENGINE_START */}'
export const FORM_ENGINE_END   = '{/* FORM_ENGINE_END */}'

export async function publishPartialToFile(formType: string, fieldsJsx: string): Promise<PublishResult> {
  try {
    let handle: FileSystemFileHandle | null = sessionHandles.get(formType) ?? null
    if (!handle) handle = await getStoredHandle(formType)

    if (handle) {
      let perm: PermissionState = 'denied'
      try {
        perm = await (handle as any).queryPermission({ mode: 'readwrite' })
        if (perm !== 'granted') perm = await (handle as any).requestPermission({ mode: 'readwrite' })
      } catch { /* stale handle */ }
      if (perm !== 'granted') {
        await clearHandle(formType)
        handle = null
      }
    }

    if (!handle) {
      handle = await (window as any).showSaveFilePicker({
        suggestedName: FORM_TYPE_FILE_NAMES[formType] ?? `${formType}-form.tsx`,
        types: [{ description: 'TypeScript React', accept: { 'text/plain': ['.tsx', '.ts'] } }],
      }) as FileSystemFileHandle
      await storeHandle(formType, handle)
    }

    sessionHandles.set(formType, handle)

    // Read the existing file and patch only the section between the markers
    const existingText: string = await (handle as any).getFile().then((f: File) => f.text())
    const startIdx = existingText.indexOf(FORM_ENGINE_START)
    const endIdx   = existingText.indexOf(FORM_ENGINE_END)

    if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) {
      // Safety: refuse to write if the target file doesn't have the markers —
      // writing just the fields JSX would destroy the component shell.
      return { success: false, error: 'markers_not_found' }
    }

    const finalContent =
      existingText.slice(0, startIdx + FORM_ENGINE_START.length) +
      '\n' + fieldsJsx + '\n' +
      existingText.slice(endIdx)

    const writable = await (handle as any).createWritable()
    await writable.write(finalContent)
    await writable.close()

    return { success: true }
  } catch (err: any) {
    if (err?.name === 'AbortError') return { success: false, error: 'cancelled' }
    return { success: false, error: String(err) }
  }
}

// Schema persistence per form type in localStorage
export function saveSchema(formType: string, schema: object): void {
  try { localStorage.setItem(`formEngine_schema_${formType}`, JSON.stringify(schema)) } catch { /* ignore */ }
}

export function loadSchema(formType: string): object | null {
  try {
    const raw = localStorage.getItem(`formEngine_schema_${formType}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// Published schema — written by Publish button, read by live form components.
// Saves to both localStorage (sync read) and IndexedDB (survives localStorage.clear()).
export function publishSchemaOnly(formType: string, schema: object): void {
  try { localStorage.setItem(`formEngine_published_${formType}`, JSON.stringify(schema)) } catch { /* ignore */ }
  savePublishedSchemaToIDB(formType, schema)
}

export function loadPublishedSchema(formType: string): { containersData: any[]; selectedLayout: Record<string, number[]> } | null {
  try {
    const raw = localStorage.getItem(`formEngine_published_${formType}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
